import React, { useState, useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { EmploymentContent } from '../EmploymentContent'
import { createEmploymentT } from '../../../utils/employmentTranslations'
import { type EmploymentAnalysis } from './employmentService'
import { OverviewTab } from './components/tabs/OverviewTab'
import { EconomicSectorsTab } from './components/tabs/EconomicSectorsTab'
import { EmploymentGrowthTab } from './components/tabs/EmploymentGrowthTab'
import { BusinessActivityTab } from './components/tabs/BusinessActivityTab'

type LocationType = 'city' | 'district' | 'radius' | 'walk' | 'bicycle' | 'car'

export interface EmploymentSectionProps {
  entity: {
    id: string
    geo_center?: {
      lat: number
      lon: number
    }
    municipality_name?: string
    district_name?: string
  }
  userId: string
  onDataFetched: (data: EmploymentAnalysis) => void
  getEmploymentData: () => EmploymentAnalysis | null
  updateEmploymentData: (
    entity: any,
    userId: string,
    params: {
      locationType: LocationType
      radiusDistance: number
      walkTime: number
      bicycleTime: number
      carTime: number
    }
  ) => Promise<EmploymentAnalysis | null>
  /** When true, hides filter controls and shows only the current filter value */
  readOnly?: boolean
}

export const EmploymentSection: React.FC<EmploymentSectionProps> = ({
  entity,
  userId,
  onDataFetched,
  getEmploymentData,
  updateEmploymentData,
  readOnly = false,
}) => {
  const { t: rawT, i18n } = useTranslation()
  const t = createEmploymentT(rawT, i18n)
  // Filter state
  const [activeTab, setActiveTab] = useState('overview')
  const [locationType, setLocationType] = useState<LocationType>('city')
  const [radiusDistance, setRadiusDistance] = useState(500)
  const [walkTime, setWalkTime] = useState(10)
  const [bicycleTime, setBicycleTime] = useState(15)
  const [carTime, setCarTime] = useState(10)

  // Loading and error state
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Track fetched params to prevent duplicate requests
  const paramsHashRef = useRef<string>('')

  const fetchEmployment = useCallback(async () => {
    if (!entity?.id || !entity?.geo_center || !userId) {
      return
    }

    const paramsHash = JSON.stringify({
      entityId: entity.id,
      locationType,
      radiusDistance,
      walkTime,
      bicycleTime,
      carTime,
    })

    // Skip if we already fetched with these exact params
    if (paramsHashRef.current === paramsHash) {
      return
    }

    setLoading(true)
    setError(null)

    try {
      const analysis = await updateEmploymentData(entity, userId, {
        locationType,
        radiusDistance,
        walkTime,
        bicycleTime,
        carTime,
      })

      if (analysis) {
        onDataFetched(analysis)
      }
      paramsHashRef.current = paramsHash
    } catch (err) {
      console.error('Error fetching employment data:', err)
      setError(err instanceof Error ? err.message : t('loading.fetchFailed'))
    } finally {
      setLoading(false)
    }
  }, [
    entity,
    userId,
    locationType,
    radiusDistance,
    walkTime,
    bicycleTime,
    carTime,
    updateEmploymentData,
    onDataFetched,
  ])

  // Fetch when filters change
  useEffect(() => {
    fetchEmployment()
  }, [fetchEmployment])

  const data = getEmploymentData()

  const renderTabContent = useCallback(() => {
    switch (activeTab) {
      case 'overview':
        return <OverviewTab data={data} t={t} />
      case 'sectors':
        return <EconomicSectorsTab data={data} t={t} />
      case 'growth':
        return <EmploymentGrowthTab data={data} t={t} />
      case 'business':
        return <BusinessActivityTab data={data} t={t} />
      default:
        return <OverviewTab data={data} t={t} />
    }
  }, [activeTab, data, t])

  const getLocationLabel = useCallback(() => {
    if (!entity) return t('filters.selectLocation')

    switch (locationType) {
      case 'city':
        return entity.municipality_name || t('filters.city')
      case 'district':
        return entity.district_name || t('filters.district')
      case 'walk':
        return t('filters.minWalk', { minutes: walkTime })
      case 'bicycle':
        return t('filters.minBike', { minutes: bicycleTime })
      case 'car':
        return t('filters.minDrive', { minutes: carTime })
      default:
        return t('filters.selectLocation')
    }
  }, [entity, locationType, walkTime, bicycleTime, carTime, t])

  return (
    <EmploymentContent
      data={data}
      loading={loading}
      error={error}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      locationType={locationType}
      onLocationTypeChange={setLocationType}
      radiusDistance={radiusDistance}
      onRadiusDistanceChange={setRadiusDistance}
      walkTime={walkTime}
      onWalkTimeChange={setWalkTime}
      bicycleTime={bicycleTime}
      onBicycleTimeChange={setBicycleTime}
      carTime={carTime}
      onCarTimeChange={setCarTime}
      renderTabContent={renderTabContent}
      getLocationLabel={getLocationLabel}
      cityName={entity?.municipality_name}
      districtName={entity?.district_name}
      readOnly={readOnly}
    />
  )
}
